import React, { useState } from 'react';
import { API_BASE_URL } from '../config';
import logo from '../../../images/logo_remove_background.png';
import './StatusTrack.css';

const STATUSES = ['Pending to String', 'Stringing', 'Ready to Collect', 'Collected'];

const statusStep = (status) => {
    const idx = STATUSES.findIndex(s => s.toLowerCase() === (status || '').toLowerCase());
    return idx === -1 ? 0 : idx;
};

const fmt = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
};

const StatusTrack = () => {
    const [orderRef, setOrderRef] = useState('');
    const [contact, setContact] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [orders, setOrders] = useState(null);
    const [expandedIdx, setExpandedIdx] = useState(null);

    const handleTrack = async (e) => {
        e.preventDefault();
        if (!orderRef.trim() || !contact.trim()) {
            setError('Please enter both your order reference and contact number.');
            return;
        }
        setLoading(true);
        setError('');
        setOrders(null);
        try {
            const res = await fetch(
                `${API_BASE_URL}/track-order?ref=${encodeURIComponent(orderRef.trim())}&contact=${encodeURIComponent(contact.trim())}`
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Order not found');
            setOrders(data.orders);
            setExpandedIdx(0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="st-page">
            <div className="st-hero">
                <h1>Order Status</h1>
                <p>Track the progress of your racquet stringing</p>
            </div>

            <div className="st-container">
                {/* Lookup card */}
                <div className="st-lookup-card">
                    <img src={logo} alt="Takumi" className="st-lookup-logo" />
                    <h2>Track Your Order</h2>
                    <p className="st-lookup-hint">Enter your order reference and the contact number you registered with.</p>

                    <form className="st-form" onSubmit={handleTrack}>
                        <div className="st-form-group">
                            <label htmlFor="st-ref">Order Reference</label>
                            <input
                                id="st-ref"
                                type="text"
                                placeholder="e.g. TT1000"
                                value={orderRef}
                                onChange={e => setOrderRef(e.target.value.toUpperCase())}
                                className="st-input"
                                autoComplete="off"
                            />
                        </div>
                        <div className="st-form-group">
                            <label htmlFor="st-contact">Contact Number</label>
                            <input
                                id="st-contact"
                                type="tel"
                                placeholder="e.g. 90688780"
                                value={contact}
                                onChange={e => setContact(e.target.value)}
                                className="st-input"
                            />
                        </div>
                        {error && <div className="st-error">{error}</div>}
                        <button type="submit" className="st-btn" disabled={loading}>
                            {loading ? 'Searching…' : 'Track Order'}
                        </button>
                    </form>
                </div>

                {/* Results */}
                {orders && (
                    <div className="st-results">
                        <div className="st-results-header">
                            <div>
                                <span className="st-ref-badge">{orders[0]?.order_group_ref}</span>
                                <span className="st-customer-name">👤 {orders[0]?.customer_name}</span>
                            </div>
                            <span className="st-racquet-count">{orders.length} racquet{orders.length !== 1 ? 's' : ''}</span>
                        </div>

                        {orders.map((order, idx) => {
                            const step = statusStep(order.status);
                            const isCollected = order.status === 'Collected';
                            const isReady = order.status === 'Ready to Collect';
                            const isOpen = expandedIdx === idx;

                            return (
                                <div key={order.order_id} className={`st-order-card ${isCollected ? 'st-collected' : ''} ${isReady ? 'st-ready' : ''} ${isOpen ? 'st-open' : 'st-closed'}`}>
                                    {/* Clickable header — always visible */}
                                    <div
                                        className="st-card-header st-card-header-toggle"
                                        onClick={() => setExpandedIdx(isOpen ? null : idx)}
                                    >
                                        <span className="st-racquet-num">Racquet {idx + 1}</span>
                                        <span className="st-header-meta">
                                            {order.racquet_brand} {order.racquet_model}
                                        </span>
                                        {order.express_service ? <span className="st-express-badge">⚡ Express</span> : null}
                                        <span className={`st-status-pill st-status-${(order.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                                            {order.status}
                                        </span>
                                        <span className={`st-chevron ${isOpen ? 'st-chevron-open' : ''}`}>▾</span>
                                    </div>

                                    {/* Collapsible body */}
                                    {isOpen && (
                                        <div className="st-card-body">
                                            {/* Progress stepper */}
                                            <div className="st-stepper">
                                                {STATUSES.map((s, i) => (
                                                    <React.Fragment key={s}>
                                                        <div className={`st-step ${i < step ? 'st-step-done' : ''} ${i === step ? 'st-step-active' : ''}`}>
                                                            <div className="st-dot">
                                                                {i < step ? '✓' : i + 1}
                                                            </div>
                                                            <div className="st-step-label">{s}</div>
                                                        </div>
                                                        {i < STATUSES.length - 1 && (
                                                            <div className={`st-connector ${i < step ? 'st-connector-done' : ''}`} />
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>

                                            {/* Detail grid */}
                                            <div className="st-detail-grid">
                                                <div className="st-detail">
                                                    <span className="st-dl">Racquet</span>
                                                    <span className="st-dv">{order.racquet_brand} {order.racquet_model}</span>
                                                </div>
                                                <div className="st-detail">
                                                    <span className="st-dl">String</span>
                                                    <span className="st-dv">{order.string_brand}{order.string_model ? ` ${order.string_model}` : ''}</span>
                                                </div>
                                                {order.color && (
                                                    <div className="st-detail">
                                                        <span className="st-dl">Color</span>
                                                        <span className="st-dv">{order.color}</span>
                                                    </div>
                                                )}
                                                {order.tension && (
                                                    <div className="st-detail">
                                                        <span className="st-dl">Tension</span>
                                                        <span className="st-dv">{order.tension} lbs</span>
                                                    </div>
                                                )}
                                                {order.dt && (
                                                    <div className="st-detail">
                                                        <span className="st-dl">DT</span>
                                                        <span className="st-dv">{order.dt}</span>
                                                    </div>
                                                )}
                                                {order.stringer_name && (
                                                    <div className="st-detail">
                                                        <span className="st-dl">Stringer</span>
                                                        <span className="st-dv st-stringer">🧵 {order.stringer_name}</span>
                                                    </div>
                                                )}
                                                {order.order_date && (
                                                    <div className="st-detail">
                                                        <span className="st-dl">Order Date</span>
                                                        <span className="st-dv">{fmt(order.order_date)}</span>
                                                    </div>
                                                )}
                                                {order.collection_date && (
                                                    <div className="st-detail">
                                                        <span className="st-dl">Expected Collection</span>
                                                        <span className="st-dv">{fmt(order.collection_date)}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {isReady && (
                                                <div className="st-ready-banner">
                                                    🎉 Your racquet is ready! Please come collect it at the shop.
                                                </div>
                                            )}
                                            {isCollected && (
                                                <div className="st-collected-banner">
                                                    ✅ Collected — enjoy your game!
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StatusTrack;
